import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import httpclient from "../../../../Utils";

const formFields = {
  depo: "",
  depoCode: "", // Added depoCode field
  state: "",
  city: "",
  postCode: "",
  phone: "",
  fullAddress: "",
};

const validateForm = (formData) => {
  const errors = {};

  if (!formData.depo?.trim()) {
    errors.depo = "Depot name is required";
  }

  if (!formData.state?.trim()) {
    errors.state = "State is required";
  }

  if (!formData.postCode?.trim()) {
    errors.postCode = "Post code is required";
  }

  if (formData.phone) {
    if (!/^\d*$/.test(formData.phone)) {
      errors.phone = "Phone number must contain only numbers";
    } else if (formData.phone.length > 10) {
      errors.phone = "Phone number cannot exceed 10 digits";
    }
  }

  return errors;
};

function EditDepotModal({ open, onClose, depotData, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(formFields);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  // Set form data when depotData changes
  useEffect(() => {
    if (depotData) {
      setFormData({
        depo: depotData.depo || "",
        depoCode: depotData.depo_code || "", // Include depoCode in form state
        state: depotData.state || "",
        city: depotData.city || "",
        postCode: depotData.post_code || "",
        phone: depotData.phone || "",
        fullAddress: depotData.full_address || "",
      });
    }
  }, [depotData]);

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    
    // For phone field, only allow numbers
    if (field === "phone") {
      value = value.replace(/[^0-9]/g, '');
      // Limit to 10 digits
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field changes
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.values(errors)[0];
      showNotification("error", firstError);
      return;
    }

    if (!depotData?.id) {
      showNotification("error", "Depot ID not found");
      return;
    }

    try {
      setIsSubmitting(true);

      const endpoint = `delivery/depo/update/${depotData.id}?checkError=1`;

      const payload = {
        depo: formData.depo,
        depo_code: formData.depoCode, // Include depo_code in payload
        state: formData.state,
        city: formData.city,
        post_code: formData.postCode,
        phone: formData.phone,
        full_address: formData.fullAddress,
      };

      const { data } = await httpclient.post(endpoint, payload);

      if (data.status === 200) {
        showNotification("success", data.message);
        
        // Close modal and refresh data without page reload
        setTimeout(() => {
          onClose();
          // Trigger a refresh in the parent component
          if (onSuccess) {
            onSuccess();
          }
        }, 1000);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormErrors({});
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        }
      }}
    >
      <DialogTitle sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        borderBottom: "1px solid #E0E0E0",
        py: 2
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Depot
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Depot Code"
                variant="outlined"
                value={formData.depoCode}
                placeholder="Depot code (non-editable)"
                disabled={true} // Makes it non-editable
                InputProps={{
                  readOnly: true, // Additional prop to ensure it's read-only
                  sx: {
                    backgroundColor: "#f5f5f5", // Light gray background to indicate non-editable
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Depot Name"
                variant="outlined"
                value={formData.depo}
                onChange={handleChange("depo")}
                error={!!formErrors.depo}
                helperText={formErrors.depo}
                placeholder="Enter depot name"
                required
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Address"
                variant="outlined"
                value={formData.fullAddress}
                onChange={handleChange("fullAddress")}
                error={!!formErrors.fullAddress}
                helperText={formErrors.fullAddress}
                placeholder="Enter complete address"
                multiline
                rows={2}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="State"
                variant="outlined"
                value={formData.state}
                onChange={handleChange("state")}
                error={!!formErrors.state}
                helperText={formErrors.state}
                placeholder="Enter state"
                required
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={formData.city}
                onChange={handleChange("city")}
                error={!!formErrors.city}
                helperText={formErrors.city}
                placeholder="Enter city"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Post Code"
                variant="outlined"
                value={formData.postCode}
                onChange={handleChange("postCode")}
                error={!!formErrors.postCode}
                helperText={formErrors.postCode}
                placeholder="Enter post code"
                required
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                value={formData.phone}
                onChange={handleChange("phone")}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                placeholder="Enter phone number (max 10 digits)"
                disabled={isSubmitting}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 10
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          borderTop: "1px solid #E0E0E0",
          gap: 2
        }}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              borderRadius: "4px",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              borderRadius: "4px",
              backgroundColor: "#7152F3",
              "&:hover": {
                backgroundColor: "#5c34fa",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
              minWidth: "100px"
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditDepotModal;